import { DOCUMENT_UPLOAD_MAX_FILE_SIZE, DocumentUploadType } from 'constants/documents'
import React, { useCallback, useEffect, useState } from 'react'
import { useGalleryDeal, useGalleryProduct } from 'components/pages/Gallery/_main/contexts'

import { AssignmentDocumentsEndpoint } from 'constants/API'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { GRAY_400 } from 'constants/styling/theme'
import { Label } from 'components/common/Label'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { QueryStatus } from 'components/common/QueryStatus'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import Stack from '@mui/material/Stack'
import { getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import { supportedDocumentFileTypes } from 'constants/misc'
import { useAuth0 } from 'utils/auth'
import { useClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { useCreateAssignmentMessageThread } from 'dataQueries/messageThreads'
import { useFileAPI } from 'components/common/FileAPI'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/** interface @Props for the ClientGalleryCreateRequestDrawer component */
interface Props {
  /** Determines if the drawer is open. */
  isOpen: boolean
  /** Whether the user can upload documents in the drawer. */
  allowUploadOfDocuments?: boolean
  /** Function to call when the drawer is closed. */
  onClose: (refetchThreads?: boolean) => void
}

/** File API scope for the thread drawer upload */
const thread_drawer_upload_scope = 'CREATE_THREAD_DRAWER'

/**
 * @component
 * Drawer for creating a new request thread in the client gallery.
 *
 * @example
 * <ClientGalleryCreateRequestDrawer isOpen={true} onClose={() => setOpenDrawer(false)} allowUploadOfDocuments={true} />
 */
export const ClientGalleryCreateRequestDrawer: React.FC<Props> = ({
  isOpen,
  allowUploadOfDocuments,
  onClose,
}) => {

  const { t } = useTranslation(['threads', 'actions', 'common'])
  const { roles } = useAuth0()

  const { assignmentId } = useClientGallery()
  const createAssignmentMessageThread = useCreateAssignmentMessageThread()
  const { refetchTotalRequestsCount } = useGalleryDeal()
  const { isEnergyCertificateProduct } = useGalleryProduct()
  const { spawnSuccessToast } = useSnackbar()
  const { userPropertyWorkspaceId } = useUserData()

  const [newThreadTitle, setNewThreadTitle] = useState('')
  const [newThreadMessage, setNewThreadMessage] = useState('')
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false)

  /** Initializes File API for the request drawer */
  const drawerThreadUpload = useFileAPI(thread_drawer_upload_scope, {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => {
      return api.post(
        roles.isClient ? AssignmentDocumentsEndpoint.POST_UPLOAD_URL_WITH_WORKSPACE : AssignmentDocumentsEndpoint.POST_UPLOAD_URL,
        { assignmentId, workspaceId: userPropertyWorkspaceId },
        {
          filename: fileEntry.fileObject.name,
          contentType: fileEntry.fileObject.type,
          type: DocumentUploadType.INPUT,
        },
        true
      )
    }),
    deleteHandler: (fileEntry, api) => api.delete(
      roles.isClient ? AssignmentDocumentsEndpoint.DELETE_DOCUMENT_WITH_WORKSPACE : AssignmentDocumentsEndpoint.DELETE_DOCUMENT,
      { assignmentId, workspaceId: userPropertyWorkspaceId },
      true,
      {
        params: {
          type: DocumentUploadType.INPUT,
          filename: fileEntry.gcFilename,
        }
      }
    )
  })

  /** Handles closing the drawer, clear the input fields and purge files. */
  const handleCloseDrawer = useCallback((refetchThreads?: boolean) => {
    setNewThreadTitle('')
    setNewThreadMessage('')

    drawerThreadUpload.purgeFilesScope(thread_drawer_upload_scope)
    createAssignmentMessageThread.reset()

    onClose(refetchThreads)
  }, [createAssignmentMessageThread, drawerThreadUpload, onClose])

  /** Handles the creation of a new request thread. */
  const handleCreateRequestThread = useCallback(() => {
    const newThreadFilenames = drawerThreadUpload.allFilesArray.map((file) => file.gcFilename).filter((filename): filename is string => !!filename)

    createAssignmentMessageThread.mutate(
      {
        assignmentId,
        title: newThreadTitle,
        externalReference: isEnergyCertificateProduct ? assignmentId : null,
        initialMessage: {
          content: newThreadMessage,
          attachmentFilenames: newThreadFilenames,

        }
      },
      {
        onSuccess: () => {
          spawnSuccessToast(t('create_thread.success'))
          refetchTotalRequestsCount()
          handleCloseDrawer(true)
        }
      }
    )

  }, [assignmentId, createAssignmentMessageThread, drawerThreadUpload.allFilesArray, handleCloseDrawer, isEnergyCertificateProduct, newThreadMessage, newThreadTitle, refetchTotalRequestsCount, spawnSuccessToast, t])

  const onCheckFileSize = (fileSize: number) => (fileSize > 0 && fileSize <= DOCUMENT_UPLOAD_MAX_FILE_SIZE)

  // Disable the submit button if the title or message are empty
  useEffect(() => {
    setDisabledSubmitButton(!newThreadTitle || !newThreadMessage)
  }, [newThreadTitle, newThreadMessage])

  // Set the default thread title for energy certificate products
  useEffect(() => {
    if (isOpen && isEnergyCertificateProduct) {
      setNewThreadTitle(t('new_thread_title', { assignmentId }))
    }
  }, [assignmentId, isEnergyCertificateProduct, isOpen, t])

  return (
    <ResponsiveDrawer
      backdrop="blurred"
      isOpen={isOpen}
      onClose={handleCloseDrawer}
      title={t('new_request_title')}
      actionsSlot={
        <Stack direction="row" gap={1}>

          <MUIButton type="secondaryBorder" onClick={() => handleCloseDrawer()}>
            {t('actions:Cancel')}
          </MUIButton>

          <MUIButton
            disabled={disabledSubmitButton || !createAssignmentMessageThread.isIdle}
            isLoading={createAssignmentMessageThread.isPending}
            onClick={handleCreateRequestThread}
          >
            {t('send_request')}
          </MUIButton>

        </Stack>
      }
    >
      <Stack gap={2}>

        <MUIInputField
          required
          label={t('create_thread.label_title')}
          value={newThreadTitle}
          disabled={isEnergyCertificateProduct}
          onChange={(e) => setNewThreadTitle(e.target.value)}
        />

        <MUIInputField
          required
          isMultiline={true}
          value={newThreadMessage}
          label={t('create_thread.label_message')}
          onChange={(e) => setNewThreadMessage(e.target.value)}
        />

        {allowUploadOfDocuments && (
          <Stack gap={0.6}>
            <Label text={t('create_thread.label_attachments')} />

            <FileAPIUpload
              boxElevation="xs"
              borderBoxColor={GRAY_400}
              files={drawerThreadUpload.allFilesArray}
              maxNumberOfFiles={5}
              acceptedFileTypes={[...supportedDocumentFileTypes]} // Only PDF 
              uploadHandler={(acceptedFiles) => drawerThreadUpload.uploadFiles(acceptedFiles)}
              onDelete={(fileId) => drawerThreadUpload.deleteFiles([fileId])}
              onCheckFileSize={onCheckFileSize}
              fileSizeCheckFailedWarning={t('upload_files:file_size_check_failed_with_limits', { minSize: '0B', maxSize: '5MB' })}
            />
          </Stack>
        )}

      </Stack>

      <QueryStatus
        query={createAssignmentMessageThread}
        spaceTopRem={2}
        showStates={['error']}
      />

    </ResponsiveDrawer>
  )
}
